import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { useAppSelector } from '@/store';
import { usePaymentProcessing } from '@/features/payments/hooks/usePaymentProcessing';

type Params = {
  leaseId: number;
  residentId?: number;
  amount?: number;
  sourceId?: number;
  onSuccess?: string; // Screen to navigate to on success
  onFailure?: string; // Screen to navigate to on failure
  onTimeout?: string; // Screen to navigate to on timeout
  paymentId?: string; // Optional: payment id for Yardi import/polling when available
};

export const TransactionProcessingScreen: React.FC = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const DESKTOP_COLORS = useDesktopColors();
  const { colors } = useThemeColors();

  const {
    leaseId,
    residentId,
    amount,
    sourceId,
    onSuccess = 'TransactionReceived',
    onFailure = 'PaymentSummaryModal',
    onTimeout = 'TransactionTimeout',
    paymentId,
  } = (route.params || {}) as Params;

  const { message, currentMessageIndex, totalSteps, progressPercent, isLoading } = usePaymentProcessing({
    leaseId,
    residentId,
    amount,
    sourceId,
    paymentId,
    onSuccess: ({ leaseId: lId, residentId: rId, amount: amt, paymentMethod, paymentId: pId, retref, authcode }) => {
      navigation.replace(onSuccess, {
        leaseId: lId,
        residentId: rId,
        amount: amt,
        paymentMethod,
        paymentId: pId,
        retref,
        authcode,
      });
    },
    onFailure: () => {
      navigation.replace(onFailure, { leaseId });
    },
    onTimeout: () => {
      navigation.replace(onTimeout, { leaseId });
    },
  });

  return (
    <UnifiedModal
      visible={true}
      variant="dialog"
      dialogSize="default"
      onClose={() => navigation.replace(onFailure, { leaseId })}
      title="Processing Payment"
      dismissOnBackdrop={false}
    >
      <View style={styles.content}>
        <ActivityIndicator
          size="large"
          color={DESKTOP_COLORS.BUTTON_PRIMARY}
          style={styles.spinner}
        />

        <Text style={[styles.message, { color: DESKTOP_COLORS.TEXT_SECONDARY }]}>
          {message}
        </Text>

        <View style={styles.progressContainer}>
          <View style={[styles.progressBar, { backgroundColor: colors.activityIcon.bg }]}>
            <View
              style={[
                styles.progressFill,
                { width: `${progressPercent}%`, backgroundColor: DESKTOP_COLORS.BUTTON_PRIMARY }
              ]}
            />
          </View>
          <Text style={[styles.progressText, { color: DESKTOP_COLORS.TEXT_TERTIARY }]}>
            Step {currentMessageIndex + 1} of {totalSteps}
          </Text>
        </View>

        <Text style={[styles.disclaimer, { color: DESKTOP_COLORS.TEXT_TERTIARY }]}>
          Please do not close this window or navigate away while your payment is being processed.
        </Text>
      </View>
    </UnifiedModal>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  spinner: {
    marginBottom: 32,
  },
  message: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
    paddingHorizontal: 16,
  },
  progressContainer: {
    width: '100%',
    maxWidth: 300,
    marginBottom: 32,
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  progressText: {
    fontSize: 12,
    textAlign: 'center',
  },
  disclaimer: {
    fontSize: 12,
    textAlign: 'center',
    fontStyle: 'italic',
    paddingHorizontal: 16,
  },
});

export default TransactionProcessingScreen;
